import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiSearch,
} from 'react-icons/fi';
import {
  LuPlus,
  LuCircleUser,
  LuMegaphone,
  LuLayoutDashboard,
} from 'react-icons/lu';
import { useChat } from '../../chat/ChatProvider';
import MilletWordmark from '../brand/MilletWordmark';
import MessageIcon from '../ui/MessageIcon';
import {
  NotificationButton,
  HeaderIconButton,
  TooltipWrapper,
  Tooltip,
} from '../../styles/navigation/HeaderStyles';
import {
  MobileBrandBar,
  MobileBrandBarTopRow,
  MobileBrandBarSearch,
  MobileBrandLogoLink,
  MobileBrandBarActions,
  BottomNavWrapper,
  BottomNavItem,
  BottomNavIcon,
  BottomNavLabel,
} from '../../styles/navigation/BottomNavigationStyles';
import HeaderMarketplaceSearch from './HeaderMarketplaceSearch';
import useMarketplaceChromeSearch from '../../hooks/marketplace/useMarketplaceChromeSearch';

import {
  isMarketplaceDetailRoute,
  shouldShowBottomNavigation,
  shouldShowMobileBrandBar,
} from '../../utils/navigation/bottomNavigationVisibility';

const MOBILE_BOTTOM_NAV_BODY_CLASS = 'has-mobile-bottom-nav';
const MOBILE_BRAND_BAR_BODY_CLASS = 'has-mobile-brand-bar';
const MOBILE_BRAND_BAR_COMPACT_BODY_CLASS = 'has-mobile-brand-bar-compact';

function BottomNavigation({ isAuthenticated }) {
  const [userType, setUserType] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const chatContext = useChat();
  const toggleChat = chatContext?.toggle || (() => {});
  const unreadCount = chatContext?.unreadCount || 0;
  const {
    draft: searchDraft,
    setDraft: setSearchDraft,
    submit: submitSearch,
    clear: clearSearch,
    selectSuggestion,
    placeholder: searchPlaceholder,
    hideMarketplaceFacets,
  } = useMarketplaceChromeSearch(userType);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserType(payload.user_type);
      } catch (error) {
        console.error('Invalid token format:', error);
        setUserType(null);
      }
    } else {
      setUserType(null);
    }
  }, [isAuthenticated]);

  const isMarketplaceDetailPage = isMarketplaceDetailRoute(location.pathname);

  const showBottomNav = useMemo(() => shouldShowBottomNavigation({
    pathname: location.pathname,
    isAuthenticated,
    userType,
  }), [location.pathname, isAuthenticated, userType]);

  const showMobileBrandBar = useMemo(() => shouldShowMobileBrandBar({
    pathname: location.pathname,
    isAuthenticated,
    userType,
  }), [location.pathname, isAuthenticated, userType]);
  const showMobileBrandBarSearch = Boolean(isAuthenticated);
  const compactMobileBrandBar = showMobileBrandBar && !showMobileBrandBarSearch;

  useEffect(() => {
    if (!showMobileBrandBar) {
      document.body.classList.remove(MOBILE_BRAND_BAR_BODY_CLASS);
      document.body.classList.remove(MOBILE_BRAND_BAR_COMPACT_BODY_CLASS);
      return undefined;
    }
    document.body.classList.add(MOBILE_BRAND_BAR_BODY_CLASS);
    if (compactMobileBrandBar) {
      document.body.classList.add(MOBILE_BRAND_BAR_COMPACT_BODY_CLASS);
    } else {
      document.body.classList.remove(MOBILE_BRAND_BAR_COMPACT_BODY_CLASS);
    }
    return () => {
      document.body.classList.remove(MOBILE_BRAND_BAR_BODY_CLASS);
      document.body.classList.remove(MOBILE_BRAND_BAR_COMPACT_BODY_CLASS);
    };
  }, [showMobileBrandBar, compactMobileBrandBar]);

  useEffect(() => {
    if (!showBottomNav) {
      document.body.classList.remove(MOBILE_BOTTOM_NAV_BODY_CLASS);
      return undefined;
    }
    document.body.classList.add(MOBILE_BOTTOM_NAV_BODY_CLASS);
    return () => document.body.classList.remove(MOBILE_BOTTOM_NAV_BODY_CLASS);
  }, [showBottomNav]);

  const handleNavigate = (path) => (event) => {
    event.preventDefault();
    window.scrollTo(0, 0);
    navigate(path);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogoClick = (event) => {
    event.preventDefault();
    window.scrollTo(0, 0);
    if (location.pathname === '/') {
      window.location.href = '/';
    } else {
      navigate('/');
    }
  };

  if (!showBottomNav) {
    return null;
  }

  // Brand navigation items (using outlined icons)
  const brandItems = [
    {
      icon: FiHome,
      label: 'Home',
      path: '/',
    },
    {
      icon: FiSearch,
      label: 'Influencers',
      path: '/marketplace/influencers',
    },
    {
      icon: LuLayoutDashboard,
      label: 'Dashboard',
      path: '/dashboard/overview',
    },
  ];

  // Influencer navigation items (using outlined icons)
  const influencerItems = [
    {
      icon: FiHome,
      label: 'Home',
      path: '/',
    },
    {
      icon: LuMegaphone,
      label: 'Campaigns',
      path: '/marketplace/campaigns',
    },
    {
      icon: LuLayoutDashboard,
      label: 'Dashboard',
      path: '/dashboard/overview',
    },
  ];

  // Unauthenticated navigation items
  const unauthenticatedItems = [
    {
      icon: FiHome,
      label: 'Home',
      path: '/',
    },
    {
      icon: FiSearch,
      label: 'Explore',
      path: '/marketplace/influencers',
    },
    {
      icon: LuCircleUser,
      label: 'Log in',
      path: '/login',
    },
  ];

  let navItems;
  if (!isAuthenticated) {
    navItems = unauthenticatedItems;
  } else if (userType === 'brand') {
    navItems = brandItems;
  } else {
    navItems = influencerItems;
  }

  return (
    <>
      {!isMarketplaceDetailPage && (
        <MobileBrandBar $compact={compactMobileBrandBar}>
          <MobileBrandBarTopRow>
            <MobileBrandLogoLink href="/" onClick={handleLogoClick} aria-label="Millet home">
              <MilletWordmark size="header" aria-hidden />
            </MobileBrandLogoLink>
            {isAuthenticated && (
              <MobileBrandBarActions>
                <TooltipWrapper>
                  <NotificationButton
                    onClick={toggleChat}
                    aria-label={unreadCount > 0 ? `Open messages, ${unreadCount} unread` : 'Open messages'}
                    type="button"
                  >
                    <MessageIcon />
                    {unreadCount > 0 && (
                      <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                    )}
                  </NotificationButton>
                  <Tooltip>Messages</Tooltip>
                </TooltipWrapper>
                {userType === 'brand' && (
                  <TooltipWrapper $alignRight>
                    <HeaderIconButton
                      onClick={handleNavigate('/dashboard/campaigns/create')}
                      aria-label="Post new campaign"
                      type="button"
                    >
                      <span className="header-action-btn-icon">
                        <LuPlus aria-hidden strokeWidth={1.75} />
                      </span>
                    </HeaderIconButton>
                    <Tooltip $alignRight>Post Campaign</Tooltip>
                  </TooltipWrapper>
                )}
              </MobileBrandBarActions>
            )}
          </MobileBrandBarTopRow>
          {showMobileBrandBarSearch ? (
            <MobileBrandBarSearch>
              <HeaderMarketplaceSearch
                value={searchDraft}
                onChange={setSearchDraft}
                onSubmit={submitSearch}
                onClear={clearSearch}
                onSelectSuggestion={selectSuggestion}
                userType={userType}
                placeholder={searchPlaceholder}
                inputId="mobile-marketplace-search"
                dense
                hideMarketplaceFacets={hideMarketplaceFacets}
              />
            </MobileBrandBarSearch>
          ) : null}
        </MobileBrandBar>
      )}
      <BottomNavWrapper>
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <BottomNavItem
              key={item.path}
              href={item.path}
              onClick={handleNavigate(item.path)}
              $isActive={isActive(item.path)}
            >
              <BottomNavIcon>
                <Icon />
              </BottomNavIcon>
              <BottomNavLabel>{item.label}</BottomNavLabel>
            </BottomNavItem>
          );
        })}
      </BottomNavWrapper>
    </>
  );
}

export default BottomNavigation;
